import { z } from 'zod'
import { hasIncludedDisposition, NO_DISPOSITION_SELECTED_MESSAGE } from './eligibility'
import { isValidSourcingPair } from './sourcing'

// ─── GCC Outsourcing Scenario ─────────────────────────────────────────────────

export const GccOutsourcingScenarioSchema = z.enum(['High', 'Medium', 'Low', 'Retained'])

export const GccCombinableScenarioSchema = z.enum(['Medium', 'Low'])

// ─── GCC Design Engine ───────────────────────────────────────────────────────

export const GccSourcingTierSchema = z.enum(['HQ', 'Markets', 'GCC-Offshore', 'GCC-Nearshore', 'MSP'])

// Same values, in the same order, as GCC_OWNERSHIPS / GCC_LOCATIONS (checked by tests)
export const GccOwnershipSchema = z.enum(['Captive-GCC', 'MSP', 'Retained'])

export const GccLocationSchema = z.enum(['HQ', 'Markets', 'Nearshore', 'Offshore'])

const INVALID_SOURCING_PAIR_MESSAGE =
  'Invalid ownership × location: Retained is HQ or Markets, Captive-GCC is Nearshore or Offshore, MSP is any location'

/** Ownership × location, rejecting pairs an ownership can't deliver from (WI 2324). */
export const GccSourcingPairSchema = z
  .object({ ownership: GccOwnershipSchema, location: GccLocationSchema })
  .refine((p) => isValidSourcingPair(p.ownership, p.location), { message: INVALID_SOURCING_PAIR_MESSAGE })

/**
 * One task entry of the Step 2 LLM response (WI 2324). The pipeline validates each
 * entry before using it; an entry that fails leaves its task at the default sourcing.
 */
export const GccStep2TaskSourcingResponseSchema = z
  .object({
    taskId: z.string().min(1).max(100),
    ownership: GccOwnershipSchema,
    location: GccLocationSchema,
    rationale: z.string().max(2000).optional(),
  })
  .refine((p) => isValidSourcingPair(p.ownership, p.location), { message: INVALID_SOURCING_PAIR_MESSAGE })

/** One capability entry of the Step 2 LLM response: the capability-level rationale. */
export const GccStep2CapabilityResponseSchema = z.object({
  capabilityId: z.string().min(1).max(100),
  rationale: z.string().max(2000),
})

const Step3ScoreSchema = z.number().int().min(1).max(5)

/**
 * One task entry of the Step 3 LLM response (WI 2325). The pipeline validates each
 * entry before using it; an entry that fails gives its task the default scores.
 */
export const GccStep3TaskScoresResponseSchema = z.object({
  taskId: z.string().min(1).max(100),
  aiReadiness: Step3ScoreSchema,
  transitionFeasibility: Step3ScoreSchema,
  rationale: z.string().max(2000).optional(),
})

/** One capability entry of the Step 3 LLM response: urgency and the capability-level rationale. */
export const GccStep3CapabilityResponseSchema = z.object({
  capabilityId: z.string().min(1).max(100),
  urgencyScore: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional(),
  rationale: z.string().max(2000),
})

const Step4SkillsSchema = z.array(z.string().min(1).max(200)).max(5)

/** One source-role flow of an end-state role (WI 2883): a role name and an FTE above 0. */
export const GccSourceRoleSchema = z.object({
  role: z.string().trim().min(1).max(200),
  fte: z.number().finite().positive(),
})

/**
 * One role entry of the Step 4 LLM response (WI 2326): the qualitative part, plus
 * the source roles (WI 2883). Headcount and cost come from the value ledger, so any
 * other numbers the LLM returns are ignored. An entry that fails leaves its role with
 * the default tag and no skills. `sourceRoles` isn't checked here: its entries are
 * checked one by one in code (GccSourceRoleSchema), so a bad flow can't cost the role
 * its tag and skills.
 */
export const GccStep4RoleQualityResponseSchema = z.object({
  roleKey: z.string().min(1).max(100),
  tag: z.enum(['Retained', 'Evolved', 'Consolidated', 'New']),
  skillsIn: Step4SkillsSchema.optional(),
  skillsOut: Step4SkillsSchema.optional(),
  skillsNetNew: Step4SkillsSchema.optional(),
  sourceRoles: z.unknown().optional(),
})

export const GccExistenceVerdictSchema = z.enum(['Keep', 'Redesign', 'Eliminate', 'New'])

export const GccDispositionTagSchema = z.enum(['Eliminate', 'Automate', 'Agentify', 'Augment', 'Human'])

export const GccEligibilityRuleSchema = z.object({
  field: z.enum(['disposition', 'sourcingTier', 'skillRarity']),
  operator: z.enum(['equals', 'not-equals', 'in', 'not-in']),
  value: z.union([z.string(), z.array(z.string())]),
}).refine(
  (r) => {
    const needsArray = r.operator === 'in' || r.operator === 'not-in'
    return needsArray ? Array.isArray(r.value) : typeof r.value === 'string'
  },
  { message: "'in'/'not-in' operators require an array value; 'equals'/'not-equals' require a string" },
)

// At least one included disposition is required; excluded tiers may be empty.
export const GccEligibilityRulesSchema = z
  .array(GccEligibilityRuleSchema)
  .refine(hasIncludedDisposition, { message: NO_DISPOSITION_SELECTED_MESSAGE })

export const GccPayRateEntrySchema = z.object({
  location: GccSourcingTierSchema,
  roleTitle: z.string().min(1).max(500),
  annualRateUSD: z.number().int().min(0),
})

export const GccPayRateTableSchema = z.array(GccPayRateEntrySchema)

export const GccProgressEventSchema = z.object({
  step: z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
  label: z.string().min(1).max(500),
  status: z.enum(['running', 'done', 'error']),
  error: z.string().max(2000).optional(),
})

export const GccJobStatusSchema = z.enum(['pending', 'running', 'succeeded', 'failed'])
